import React from 'react';
import { Spinner } from '@/components/common';
import styles from './QueueDisplay.module.css';

interface QueueDisplayProps {
  position: number;
  queueLength: number;
  estimatedWaitTime: number; // seconds
  isConnected: boolean;
  onCancel: () => void;
}

export const QueueDisplay: React.FC<QueueDisplayProps> = ({
  position,
  queueLength,
  estimatedWaitTime,
  isConnected,
  onCancel,
}) => {
  const formatWaitTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) return 'Less than 1 minute';
    if (minutes === 1) return '1 minute';
    if (minutes < 60) return `${minutes} minutes`;
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 1 && remainingMinutes === 0) return '1 hour';
    if (remainingMinutes === 0) return `${hours} hours`;
    return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`;
  };

  return (
    <div className={styles.queueDisplay} role="region" aria-labelledby="queue-title">
      <div className={styles.queueInfo}>
        <div className={styles.loadingSpinner} aria-hidden="true">
          <Spinner size="large" color="accent" />
        </div>
        
        <h2 id="queue-title" className={styles.title}>You're in the queue</h2>
        
        <div 
          className={styles.positionInfo}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-label={`Queue position ${position} of ${queueLength}, estimated wait time ${formatWaitTime(estimatedWaitTime)}`}
        >
          <div className={styles.position}>
            <span className={styles.label}>Position</span>
            <span className={styles.value} aria-label={`Position ${position} of ${queueLength}`}>
              {position} of {queueLength}
            </span>
          </div>
          
          <div className={styles.waitTime}>
            <span className={styles.label}>Estimated wait</span>
            <span className={styles.value} aria-label={`Estimated wait time ${formatWaitTime(estimatedWaitTime)}`}>
              {formatWaitTime(estimatedWaitTime)}
            </span>
          </div>
        </div>

        <div 
          className={styles.connectionStatus}
          role="status"
          aria-live="polite"
        >
          {isConnected ? (
            <span className={styles.connected}>● Connected</span>
          ) : (
            <span className={styles.reconnecting}>○ Reconnecting...</span>
          )}
        </div>

        <p className={styles.message}>
          Your call will start automatically when it's your turn. 
          You can leave this page and return later.
        </p>

        <button 
          className={styles.cancelButton}
          onClick={onCancel}
          aria-label="Cancel and leave queue"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
